import { copyText } from '@npc/platform'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { T } from '../../copy/text'
import { t } from '../../copy'
import { Card, CardTitle } from '../../components/card'
import { Button } from '../../components/button'
import { cn } from '../../components/cn'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenLoading } from '../../components/state'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Bot, CalendarClock, Check, Clipboard,
  Activity, CreditCard, DatabaseBackup, Laptop, Moon, ServerCog, ShieldCheck, Sun } from 'lucide-react'

import { LoginDevices } from './devices'
import { currentTheme, toggleTheme, type Theme } from '../../app/theme'
import { useAccountMembership, useLogout, useMe, useUpdatePassword } from '../../app/queries'
import { createLocalAISetup } from '../../app/remote-ai'
import './ops-account.css'

/**
 * 屏 `account` 你自己。2026-08-25 认证简化后只剩三件事：
 * **邮箱、修改密码、退出登录。**
 *
 * 原来的「你现在有几条路能进来」体检卡（Passkey/恢复码入口）随
 * Passkey 用户链路一起退役——登录方式只剩验证码与密码两条，
 * 没有体检可展示；恢复码不再是普通用户入口。
 */
export function AccountScreen() {
  const me = useMe()
  const membership = useAccountMembership()
  const passwordUpdate = useUpdatePassword()
  const logout = useLogout()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [theme, setTheme] = useState<Theme>(currentTheme())
  const [showLocalAISetup, setShowLocalAISetup] = useState(false)
  const [setupCopied, setSetupCopied] = useState(false)
  const [setupPrompt, setSetupPrompt] = useState('')
  const navigate = useNavigate()
  const localAISetup = useMutation({
    mutationFn: createLocalAISetup,
    onSuccess: (bundle) => {
      setSetupCopied(false)
      setSetupPrompt(bundle.prompt)
      setShowLocalAISetup(true)
    },
  })

  if (me.isPending) return <ScreenLoading shape="settings" />
  // 401 由 query-client 全局跳登录；这里只管渲染

  const hasPassword = me.data?.sign_in_paths?.paths.some(
    (path) => path.kind === 'password' && path.available,
  )

  async function submitPassword() {
    if (new TextEncoder().encode(newPassword).length > 72) {
      setPasswordError(t('auth.password.weak'))
      return
    }
    setPasswordError(null)
    try {
      await passwordUpdate.mutateAsync({
        current_password: hasPassword ? currentPassword : undefined,
        new_password: newPassword,
      })
      setCurrentPassword('')
      setNewPassword('')
    } catch {
      // The mutation error is rendered in the form.
    }
  }

  return (
    <Page className="account-theme">
      <PageHeader
        title={<T id="auth.brand.captain.name" />}
        subtitle={<span className="text-npc-ink-soft">{me.data?.email ?? ''}</span>}
        className="mb-npc-modal"
      />

      {membership.data?.capabilities.includes('device.authorize') ? (
        <Card className="mb-npc-card max-w-[560px]" data-testid="local-ai-device-authorization">
          <div className="flex items-start justify-between gap-npc-card max-sm:flex-col">
            <div className="min-w-0">
              <div className="mb-[5px] flex items-center gap-[8px]">
                <Laptop size={17} strokeWidth={1.8} aria-hidden className="text-npc-ink-soft" />
                <CardTitle className="block"><T id="account.local_ai.heading" /></CardTitle>
              </div>
              <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
                <T id="account.local_ai.hint" />
              </p>
            </div>
            <Button
              variant="primary"
              className="shrink-0"
              data-testid="local-ai-device-authorize"
              disabled={localAISetup.isPending}
              onClick={() => localAISetup.mutate()}
            >
              <T id={localAISetup.isPending ? 'account.local_ai.generating' : 'account.local_ai.cta'} />
            </Button>
          </div>
          {localAISetup.isError ? (
            <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-red-ink" data-testid="local-ai-device-error">
              <T id="account.local_ai.error" />
            </p>
          ) : null}
        </Card>
      ) : null}

      {/* 外观（导航冻结参考：外观入口归设置，不占账户菜单分区） */}
      <Card className="mb-npc-card max-w-[560px]" data-testid="settings-appearance">
        <CardTitle className="mb-npc-grid-sm block">
          <T id="nav.theme.switch" />
        </CardTitle>
        <div className="flex items-center justify-between gap-[12px]">
          <span className="text-npc-body-sm text-npc-ink-soft">
            <T id="nav.theme.current" />
          </span>
          <div className="flex gap-[6px]">
            {(['dark', 'light'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                aria-pressed={theme === opt}
                onClick={() => {
                  if (theme !== opt) setTheme(toggleTheme())
                }}
                data-testid={`theme-pill-${opt}`}
                className={cn(
                  'flex h-[32px] cursor-pointer items-center gap-[6px] rounded-[10px] border px-[14px] font-[inherit] text-npc-body-sm',
                  theme === opt
                    ? 'ac-theme-active'
                    : 'border-npc-border-soft bg-npc-panel text-npc-ink-medium hover:text-npc-ink-strong',
                )}
              >
                {opt === 'dark' ? <Moon size={13} aria-hidden /> : <Sun size={13} aria-hidden />}
                <T id={opt === 'dark' ? 'nav.theme.dark' : 'nav.theme.light'} />
              </button>
            ))}
          </div>
        </div>
        <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-ink-softer">
          <T id="nav.theme.quick.hint" />
        </p>
      </Card>

      {/* 账单与额度（v3 §4C.3）：诚实空态——数据源没接，不画假进度条 */}
      <Card className="mb-npc-card max-w-[560px]" data-testid="settings-billing">
        <CardTitle className="mb-npc-grid-sm block">
          <T id="nav.account.billing" />
        </CardTitle>
        <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
          <T id="account.billing.empty.honest" />
        </p>
      </Card>

      {/* 设置类入口（这些不进账户菜单，但入口一个不少——路由与功能全保留） */}
      <Card className="mb-npc-card max-w-[560px]" data-testid="settings-modules">
        <CardTitle className="mb-npc-grid-sm block">
          <T id="nav.account.settings" />
        </CardTitle>
        <div className="grid gap-[2px] sm:grid-cols-2">
          {SETTINGS_MODULES.map((m) => {
            const Icon = m.icon
            return (
              <button
                key={m.path}
                type="button"
                onClick={() => navigate({ to: m.path as never })}
                data-testid={`settings-module-${m.key}`}
                className="flex h-[40px] cursor-pointer items-center gap-[10px] rounded-[8px] border-none bg-transparent px-[10px] text-left text-npc-body-sm text-npc-ink-medium hover:bg-npc-surface hover:text-npc-ink-strong"  /* npc-token-allow: 导航冻结参考 NavBar.dc/MobileNav.dc 原值，整站 token 无此档 */
              >
                <Icon size={15} strokeWidth={1.8} aria-hidden className="shrink-0 text-npc-ink-softer" />
                {t(m.copyId)}
              </button>
            )
          })}
        </div>
      </Card>

      {/* 登录设备。**排在改密码上面**：他打开这一屏最常见的理由是
          「我想看看谁在登着」，而不是「我想改密码」。而且一种确认方式都
          用不上时，那个框会让他「先在下面设一个密码」——下面得真的有。 */}
      <LoginDevices />

      <Card className="max-w-[560px]">
        <CardTitle className="mb-npc-grid-sm block">
          <T id="auth.field.password.label" />
        </CardTitle>

        <form
          className="flex flex-col gap-npc-grid-sm"
          onSubmit={(event) => {
            event.preventDefault()
            void submitPassword()
          }}
        >
          {hasPassword ? (
            <label className="block">
              <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">
                <T id="auth.field.current_password.label" />
              </span>
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
                className={inputClass}
              />
            </label>
          ) : null}
          <label className="block">
            <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">
              <T id="auth.field.new_password.label" />
            </span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
              className={inputClass}
            />
          </label>
          {passwordError ? (
            <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">
              {passwordError}
            </p>
          ) : null}
          {passwordUpdate.error ? (
            <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">
              {passwordUpdate.error instanceof Error ? passwordUpdate.error.message : ''}
            </p>
          ) : null}
          {passwordUpdate.isSuccess ? (
            <p className="m-0 text-npc-caption text-npc-green-deep">
              <T id="auth.password.updated" />
            </p>
          ) : null}
          <button
            type="submit"
            disabled={newPassword.length === 0 || passwordUpdate.isPending}
            className="cursor-pointer self-start rounded-npc-button border-none bg-npc-green px-[15px] py-npc-grid-sm font-[inherit] text-npc-section-sm font-semibold text-npc-panel transition-[filter,transform] hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <T id={hasPassword ? 'auth.password.change.cta' : 'auth.password.set.cta'} />
          </button>
        </form>
      </Card>

      <div className="mt-npc-card">
        <button
          type="button"
          disabled={logout.isPending}
          data-testid="account-logout"
          onClick={() => logout.mutate()}
          className="cursor-pointer rounded-npc-inset border border-npc-border bg-npc-panel px-[15px] py-npc-grid-sm font-[inherit] text-npc-section-sm text-npc-ink-medium hover:border-npc-red-border hover:text-npc-red-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <T id="nav.account.logout" />
        </button>
      </div>

      <Modal
        open={showLocalAISetup}
        onOpenChange={setShowLocalAISetup}
        title={t('account.local_ai.modal.title')}
        description={t('account.local_ai.modal.description')}
        footer={<Button variant="primary" onClick={() => setShowLocalAISetup(false)}><T id="account.local_ai.modal.close" /></Button>}
      >
        <pre
          className="max-h-[52vh] overflow-auto whitespace-pre-wrap break-words rounded-npc-code bg-npc-surface-warm p-npc-grid-sm text-npc-meta leading-[1.7] text-npc-ink"
          data-testid="local-ai-device-prompt"
        >
          {setupPrompt}
        </pre>
        <Button
          size="sm"
          className="mt-npc-gap-sm"
          data-testid="local-ai-device-copy"
          onClick={() => {
            void copyText(setupPrompt).then((result) => {
              if (result.ok) setSetupCopied(true)
            })
          }}
        >
          {setupCopied ? <Check size={14} aria-hidden /> : <Clipboard size={14} aria-hidden />}
          <T id={setupCopied ? 'account.local_ai.copied' : 'account.local_ai.copy'} />
        </Button>
      </Modal>
    </Page>
  )
}

const inputClass =
  'block w-full rounded-npc-inset border border-npc-border bg-npc-panel px-[15px] py-npc-grid-sm ' +
  'font-[inherit] text-npc-section-sm font-normal text-npc-ink placeholder:text-npc-ink-softer'


/** 设置类模块入口（导航冻结参考纠偏：不进账户菜单，入口归设置页）。 */
const SETTINGS_MODULES = [
  { key: 'sec', path: '/security', copyId: 'nav.sec', icon: ShieldCheck },
  { key: 'backups', path: '/backups', copyId: 'ops_backup.page.title', icon: DatabaseBackup },
  { key: 'vendor', path: '/vendors', copyId: 'nav.vendor', icon: ServerCog },
  { key: 'models', path: '/models', copyId: 'nav.models', icon: Bot },
  { key: 'bridge', path: '/bridge', copyId: 'nav.bridge', icon: CreditCard },
  { key: 'alert', path: '/alerts', copyId: 'nav.alert', icon: AlertTriangle },
  { key: 'duty', path: '/duty', copyId: 'nav.duty', icon: CalendarClock },
  // 反馈（bug-reports）。`aaa951740` 原来把它塞进了**账户菜单**，
  // 而那一处的规矩写在旁边那条用例的名字里：「业务模块不出现在账户菜单
  // （安全体检/服务商/模型/AI 接口/告警），入口移到设置页」。
  // 反馈就是一个业务模块，所以它归这儿。它在壳上还有一个常驻的浮动入口
  // （BugReportLauncher），随时能开——真正常用的那条路不受影响。
  { key: 'bug_reports', path: '/bug-reports', copyId: 'nav.bug_reports', icon: Activity },
] as const
